import {
  Component,
  effect,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import {
  MatFormField,
  MatLabel,
  MatInput,
  MatSuffix,
} from '@angular/material/input';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { RevealsInvalidFields } from '../../core/reveal-invalid-fields';
import { LocationsService } from '../../services/locations.service';
import { AddressPrediction, GeoLocation } from '../../types/core';

/** Swiss house numbers: digits, optionally a short letter suffix ("12a", "3A",
 * "5b") or "bis" ("3bis"). Rejects letters alone or in front ("tz", "A3"). */
export const HOUSE_NUMBER_PATTERN = /^\d{1,4}(?:[a-zA-Z]{1,2}|bis)?$/;

/**
 * Reusable address field. The search box is the **only input**: it resolves a
 * Swiss address (by `place_id` for picked suggestions, or free text on blur) and
 * the "Ausgewählte Adresse" box below echoes Strasse & Nr./PLZ/Ort read-only. The
 * search box shows the full picked suggestion, but only the geocoder's street
 * ("Bahnhofstrasse 1") is stored as `street`: PLZ and Ort have their own columns,
 * and a stored suggestion repeated them wherever the address was printed. On
 * return the box is rebuilt from street, PLZ and Ort. The house number is kept **separate**
 * in its own column (`streetNumber`) — it's the single source of truth for the
 * number; we never parse it back out of `street`. Because many addresses have no
 * number, a **"Diese Adresse hat keine Strassennummer"** checkbox lets the user
 * mark that explicitly; otherwise a number is required (`requireNumber`, reflected
 * via `addressComplete` so parents can gate Save) and a missing one is reported on
 * the search box, where it can be fixed.
 *
 * Parents gate their Save on the outputs below, never on this component's internal
 * validity; the validators drive the red border only, and
 * {@link markInvalidFieldsTouched} lets a parent reveal it on a failed save.
 *
 * Emits a {@link GeoLocation} whose `street` is the resolved street and whose
 * `streetNumber` is the separate number. Emits are partial on a number/checkbox-
 * only edit, so **parents should merge** (`{ ...this.location, ...resolved }`).
 */
@Component({
  selector: 'app-address-autocomplete-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatCheckbox,
    MatIconButton,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col">
      <!-- The guidance sits above the box and the box runs the full width on its
           own line: next to a second field, users typed the house number there
           (or tried to) instead of into the address. -->
      <p data-testid="address-hint" class="text-sm text-bn-bright m-0 mb-2">
        Strasse + Nummer + Ort eingeben und einen Vorschlag auswählen.
      </p>
      <mat-form-field class="w-full">
        <mat-label>{{ label() }}</mat-label>
        <input
          matInput
          type="text"
          placeholder="z.B. Bahnhofstrasse 10, Einsiedeln"
          [formControl]="addressControl"
          [matAutocomplete]="auto"
          (blur)="onBlur()" />
        <!-- One element carries matSuffix: a control-flow block is projected
             into the default slot, so icons inside a bare @if would render
             below the text. -->
        <div matSuffix class="flex items-center pr-3">
          @if (searching || resolving) {
            <mat-icon class="animate-spin text-light-primary">
              progress_activity
            </mat-icon>
          } @else {
            @if (hasTypedAddress()) {
              <button
                mat-icon-button
                type="button"
                data-testid="clear-address"
                aria-label="Adresse löschen"
                (click)="clearAddress()">
                <mat-icon>close</mat-icon>
              </button>
            }
            <mat-icon class="text-bn-bright">location_on</mat-icon>
          }
        </div>
        <mat-autocomplete
          #auto="matAutocomplete"
          [displayWith]="displayAddress"
          (optionSelected)="onPlaceSelected($event)">
          @for (prediction of predictions$ | async; track prediction.place_id) {
            <mat-option [value]="prediction">
              <span class="font-medium">{{ prediction.main_text }}</span>
              <span class="text-bn-bright">
                {{ prediction.secondary_text }}
              </span>
            </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>

      <!-- Every error belongs to the box: it is the only place the user can fix
           anything, the fields below are an echo. -->
      @if (showUnresolved()) {
        <p
          data-testid="unresolved-message"
          class="text-sm text-red-600 m-0 -mt-4 mb-2">
          Adresse ungültig – bitte die Strasse eintippen und einen Vorschlag
          auswählen.
        </p>
      } @else if (addressControl.hasError('missingNumber')) {
        <p
          data-testid="missing-number-message"
          class="text-sm text-red-600 m-0 -mt-4 mb-2">
          Bitte gib auch die Strassennummer ein und wähle anschliessend die
          vollständige Adresse aus der Liste.
        </p>
      }

      <mat-checkbox [formControl]="noNumberControl" class="-mt-2 mb-2">
        Diese Adresse hat keine Strassennummer
      </mat-checkbox>

      <!-- Read-only palette (grey fields, divider border), never light blue:
           that is the app's interactivity colour and would make the echo look
           editable. -->
      @if (zipcodeControl.value || cityControl.value) {
        <div
          data-testid="selected-address"
          class="rounded-3xl border border-bn-readonly-divider px-5 pt-4 mb-4 text-bn-readonly-text">
          <p class="font-bold m-0">Ausgewählte Adresse</p>
          <p class="text-sm m-0 mb-3">Korrekt übernommen?</p>
          <!-- Strasse on its own row, PLZ and Ort below: the field sits in
               narrow columns (Stellenabo, Kontaktdaten) on wide screens too, so
               a viewport breakpoint cut the values off. -->
          <div class="grid grid-cols-5 gap-x-4">
            <mat-form-field class="bn-readonly col-span-5">
              <mat-label>
                {{ noNumberControl.value ? 'Strasse' : 'Strasse & Nr.' }}
              </mat-label>
              <input
                matInput
                type="text"
                readonly
                tabindex="-1"
                autocomplete="off"
                [formControl]="streetControl" />
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>
            <mat-form-field class="bn-readonly col-span-2">
              <mat-label>PLZ</mat-label>
              <input
                matInput
                type="text"
                readonly
                tabindex="-1"
                autocomplete="off"
                [formControl]="zipcodeControl" />
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>
            <mat-form-field class="bn-readonly col-span-3">
              <mat-label>Ort</mat-label>
              <input
                matInput
                type="text"
                readonly
                tabindex="-1"
                autocomplete="off"
                [formControl]="cityControl" />
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>
          </div>
        </div>
      }
    </div>
  `,
})
export class AddressAutocompleteFieldComponent implements RevealsInvalidFields {
  private locationsService = inject(LocationsService);

  /** Seed the confirmation fields + the address input from an already-stored address. */
  initialZipcode = input<string | undefined>();
  initialCity = input<string | undefined>();
  /** Stored Gemeinde; with a differing stored Ort it re-offers the choice. */
  initialMunicipality = input<string | undefined>();
  /** Stored street ("Bahnhofstrasse 1"); with PLZ and Ort it seeds the box. */
  initialAddress = input<string | undefined>();
  /** Stored house number; seeds the Nummer field (else split off initialAddress). */
  initialStreetNumber = input<string | undefined>();
  label = input<string>('Adresse');
  /** When true, a number is required unless "keine Nummer" is ticked. */
  requireNumber = input<boolean>(true);

  /** Emits the merged location (street = "name number", streetNumber = number). */
  locationResolved = output<GeoLocation>();
  /** True when the address was resolved (or seeded from a stored address) and a
   * number is present or "keine Nummer" is ticked (or not required). */
  addressComplete = output<boolean>();

  // Street/PLZ/Ort are required everywhere this field is embedded, so they carry
  // Validators.required — that's what makes Material render its `*` marker, the same
  // way every other mandatory field in the app gets one.
  addressControl = new FormControl<string | AddressPrediction>(
    '',
    Validators.required
  );
  // Not rendered: holds the resolved `street_number`. `Validators.required` is
  // toggled with `requireNumber` (see the effect in the constructor); ticking
  // "keine Strassennummer" disables the control.
  numberControl = new FormControl('', [
    Validators.required,
    Validators.pattern(HOUSE_NUMBER_PATTERN),
  ]);
  noNumberControl = new FormControl(false);
  // Display-only echoes of the resolved (or stored) address; nothing typed here
  // can reach the backend, so they carry no validators and no `*` marker.
  zipcodeControl = new FormControl('');
  cityControl = new FormControl('');
  streetControl = new FormControl('');
  searching = false;
  resolving = false;
  noResults = false;

  /** Last resolved/seeded location — re-emitted (merged with the current number)
   * whenever the number or checkbox changes. */
  private lastLocation: GeoLocation = {};
  /** Ortschaft as resolved (or stored); the user may switch the echo to the
   * Gemeinde, so this keeps the original for the dropdown. */
  private resolvedCity = '';
  /** Resolved "street number" as the geocoder gave it, and the full picked
   * suggestion; both lose the number while "keine Strassennummer" is ticked. */
  private resolvedStreet = '';
  private resolvedBox = '';
  /** Text we already resolved — avoids re-geocoding the same string on blur. */
  private lastResolvedQuery = '';
  /** Seed the inputs only once so a pick isn't wiped by later input changes. */
  private addressSeeded = false;
  /** Inputs arrived before any user edit — a stored address we treat as resolved. */
  private seededFromStore = false;
  /** Set on the first user edit so a parent echoing values back isn't mistaken
   * for a stored address. */
  private userInteracted = false;

  /** One token ties the keystrokes + place resolution into a single Google
   * billing session; renewed after each resolved selection. */
  private sessionToken = this.locationsService.newSessionToken();

  private autocompleteTrigger = viewChild(MatAutocompleteTrigger);

  /** Ortschaft/Gemeinde variants of the resolved address (see
   * {@link offerCityVariants}); while set they replace the Google list. */
  private variants$ = new BehaviorSubject<AddressPrediction[]>([]);
  private static readonly VARIANT_PREFIX = 'variant:';

  private googlePredictions$: Observable<AddressPrediction[]> =
    this.addressControl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      distinctUntilChanged(),
      filter((value): value is string => typeof value === 'string'),
      switchMap(value => {
        const query = value.trim();
        if (query.length < 3) {
          this.searching = false;
          this.noResults = false;
          return of<AddressPrediction[]>([]);
        }
        this.searching = true;
        return this.locationsService
          .autocompleteAddress(query, this.sessionToken)
          .pipe(
            tap(predictions => {
              this.searching = false;
              if (predictions.length === 0) {
                this.flagNoResults();
              } else {
                this.noResults = false;
              }
            }),
            catchError(() => {
              this.searching = false;
              this.flagNoResults();
              return of<AddressPrediction[]>([]);
            })
          );
      })
    );

  predictions$: Observable<AddressPrediction[]> = combineLatest([
    this.googlePredictions$,
    this.variants$,
  ]).pipe(map(([google, variants]) => (variants.length ? variants : google)));

  constructor() {
    // A typed street that was never resolved (no suggestion picked, free-text
    // lookup failed) has no canton or coordinates behind it and the backend
    // rejects it, so the search box goes red until it is resolved.
    this.addressControl.addValidators(() =>
      this.hasTypedAddress() && !this.isResolved() ? { unresolved: true } : null
    );
    // A resolved street without a number ("Bahnhofstrasse, Einsiedeln") is fixed
    // in the box too, so the box is what goes red.
    this.addressControl.addValidators(() =>
      this.isResolved() &&
      this.numberControl.enabled &&
      this.numberControl.invalid
        ? { missingNumber: true }
        : null
    );

    // Editing the text after a pick (or after loading a stored address) throws
    // the old resolution away: PLZ/Ort empty out and the box goes red until a
    // suggestion is picked again. Otherwise the stale canton/coords would let
    // the parent save a street that no longer matches them.
    this.addressControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        if (
          typeof value !== 'string' ||
          value.trim() === this.lastResolvedQuery
        ) {
          return;
        }
        this.userInteracted = true;
        this.variants$.next([]);
        if (!this.isResolved()) {
          return;
        }
        this.seededFromStore = false;
        this.lastLocation = {};
        this.zipcodeControl.setValue('', { emitEvent: false });
        this.cityControl.setValue('', { emitEvent: false });
        this.resolvedCity = '';
        this.resolvedStreet = '';
        this.streetControl.setValue('', { emitEvent: false });
        this.addressControl.updateValueAndValidity({ emitEvent: false });
        this.emitMerged();
      });

    // `requireNumber` is only bound after construction, so swap the validator in
    // once the real value has landed, or a `[requireNumber]="false"` field would
    // stay invalid and keep its `*` marker.
    effect(() => {
      if (this.requireNumber()) {
        this.numberControl.addValidators(Validators.required);
      } else {
        this.numberControl.removeValidators(Validators.required);
      }
      this.numberControl.updateValueAndValidity({ emitEvent: false });
      this.addressControl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      if (
        !this.userInteracted &&
        (this.initialZipcode() || this.initialCity())
      ) {
        this.seededFromStore = true;
      }
      // Mirror PLZ/Ort from the inputs only until the user seeds or resolves an
      // address; after that the component owns these controls (see applyResolved),
      // so a later stale input re-emit can't clobber a freshly resolved location.
      if (!this.addressSeeded) {
        this.zipcodeControl.setValue(this.initialZipcode() ?? '', {
          emitEvent: false,
        });
        this.cityControl.setValue(this.initialCity() ?? '', {
          emitEvent: false,
        });
      }
      const combined = (this.initialAddress() ?? '').trim();
      const number = (this.initialStreetNumber() ?? '').trim();
      // `userInteracted` closes the seed window on the first keystroke. Without
      // it, a contact stored with PLZ/Ort but no street never seeds, so the
      // window stays open — and the parent echoing the half-typed street back
      // into `initialAddress` lands here and is read as a stored address.
      if (!this.addressSeeded && !this.userInteracted && (combined || number)) {
        // The number comes only from its own column (`street_number`); we never
        // parse it out of `street`.
        this.numberControl.setValue(number, { emitEvent: false });
        // Addresses saved before the street was stored on its own hold the
        // whole suggestion ("Bahnhofstrasse 1, Zürich, Schweiz"); its first
        // part is the street. Re-saving stores only that part.
        const street = combined.split(',')[0].trim();
        const town = [this.initialZipcode(), this.initialCity()]
          .map(part => (part ?? '').trim())
          .filter(Boolean)
          .join(' ');
        this.resolvedStreet = street;
        this.resolvedBox = [street, town].filter(Boolean).join(', ');
        // Restore a saved "no number" address: a stored street with no number
        // means the user had ticked "keine Strassennummer". We only auto-tick
        // here on reload from saved data — never when picking a suggestion.
        if (combined && !number) {
          this.noNumberControl.setValue(true, { emitEvent: false });
          this.numberControl.disable({ emitEvent: false });
        }
        this.lastLocation = {
          zipcode: this.initialZipcode(),
          city: this.initialCity(),
          municipality: this.initialMunicipality(),
          street: street || undefined,
          streetNumber: number || undefined,
        };
        this.resolvedCity = this.initialCity() ?? '';
        this.addressSeeded = true;
        this.refreshStreetDisplay();
        this.variants$.next(this.cityVariants());
        this.emitComplete();
      }
    });

    // Editing the number or toggling the checkbox re-emits the merged location.
    // Swiss house numbers may carry a letter suffix ("12a", "5b", "3bis"), so we
    // keep digits and letters and only strip spaces/punctuation the user types.
    this.numberControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        this.userInteracted = true;
        const normalized = (value ?? '').replace(/[^0-9a-zA-Z]/g, '');
        if (normalized !== value) {
          this.numberControl.setValue(normalized, { emitEvent: false });
        }
        this.emitMerged();
      });
    this.noNumberControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(checked => {
        this.userInteracted = true;
        if (checked) {
          this.numberControl.setValue('', { emitEvent: false });
          this.numberControl.disable({ emitEvent: false });
        } else {
          // No field to retype it in any more: bring the resolved number back.
          this.numberControl.setValue(this.lastLocation.streetNumber ?? '', {
            emitEvent: false,
          });
          this.numberControl.enable({ emitEvent: false });
        }
        this.refreshStreetDisplay();
        this.addressControl.updateValueAndValidity({ emitEvent: false });
        this.emitMerged();
      });
  }

  /**
   * Ortschaft and Gemeinde differ (Schindellegi lies in Feusisberg): the same
   * address as two entries of the suggestion list, so the user picks which
   * Ort it shows. Google can't offer this up front — the Ortschaft is only
   * known once the address is resolved — so the list re-opens after the pick.
   */
  private cityVariants(): AddressPrediction[] {
    const ortschaft = this.resolvedCity.trim();
    const gemeinde = (this.lastLocation.municipality ?? '').trim();
    if (
      !ortschaft ||
      !gemeinde ||
      ortschaft.toLowerCase() === gemeinde.toLowerCase()
    ) {
      return [];
    }
    const street = this.streetControl.value ?? '';
    const zip = this.zipcodeControl.value ?? '';
    return [
      { city: ortschaft, kind: 'Ortschaft' },
      { city: gemeinde, kind: 'Gemeinde' },
    ].map(({ city, kind }) => ({
      place_id: `${AddressAutocompleteFieldComponent.VARIANT_PREFIX}${city}`,
      description: `${street}, ${zip} ${city}`.trim(),
      main_text: street,
      secondary_text: `${zip} ${city} (${kind})`.trim(),
    }));
  }

  private offerCityVariants(): void {
    const variants = this.cityVariants();
    this.variants$.next(variants);
    if (variants.length) {
      setTimeout(() => this.autocompleteTrigger()?.openPanel());
    }
  }

  private applyCityVariant(prediction: AddressPrediction): void {
    const city = prediction.place_id.slice(
      AddressAutocompleteFieldComponent.VARIANT_PREFIX.length
    );
    this.cityControl.setValue(city, { emitEvent: false });
    this.resolvedBox = prediction.description;
    this.variants$.next([]);
    this.refreshStreetDisplay();
    this.emitMerged();
  }

  displayAddress = (value: AddressPrediction | string | null): string => {
    if (!value) return '';
    return typeof value === 'string' ? value : value.description;
  };

  onPlaceSelected(event: MatAutocompleteSelectedEvent): void {
    const prediction = event.option.value as AddressPrediction;
    this.userInteracted = true;
    if (
      prediction.place_id.startsWith(
        AddressAutocompleteFieldComponent.VARIANT_PREFIX
      )
    ) {
      this.applyCityVariant(prediction);
      return;
    }
    this.resolving = true;
    this.noResults = false;
    this.locationsService
      .getLocationByPlaceId(prediction.place_id, this.sessionToken)
      .subscribe({
        next: location => {
          this.resolving = false;
          if (!location) {
            this.flagNoResults();
            return;
          }
          this.sessionToken = this.locationsService.newSessionToken();
          this.applyResolved(location, prediction.description);
          this.offerCityVariants();
        },
        error: () => {
          this.resolving = false;
          this.flagNoResults();
        },
      });
  }

  /** The X in the box: wipe the whole address so the user starts over. */
  clearAddress(): void {
    // Emits, so the edit-detection above drops the stale resolution.
    this.addressControl.setValue('');
    this.lastResolvedQuery = '';
    this.numberControl.setValue('', { emitEvent: false });
    this.addressControl.markAsUntouched();
    this.emitMerged();
  }

  /** Free-text fallback: geocode the typed string on blur so users aren't blocked. */
  onBlur(): void {
    setTimeout(() => {
      const value = this.addressControl.value;
      if (typeof value !== 'string' || this.resolving) {
        return;
      }
      const query = value.trim();
      // Higher threshold than the typeahead (>=3): a blur geocode is a single,
      // less-precise lookup, so we wait for a bit more text before spending it.
      if (query.length < 4 || query === this.lastResolvedQuery) {
        return;
      }
      this.userInteracted = true;
      this.resolving = true;
      this.noResults = false;
      this.locationsService.resolveAddressQuery(query).subscribe({
        next: location => {
          this.resolving = false;
          if (!location) {
            this.flagNoResults();
            return;
          }
          this.applyResolved(location);
          // No panel re-open on blur — the choice shows on the next focus.
          this.variants$.next(this.cityVariants());
        },
        error: () => {
          this.resolving = false;
          this.flagNoResults();
        },
      });
    }, 250);
  }

  /** Fill the echo fields, show `displayText` (the full picked suggestion, or the
   * resolved street for free-text) in the box, and keep the resolved number.
   * "keine Strassennummer" stays as the user set it unless the pick has a
   * number. */
  private applyResolved(location: GeoLocation, displayText?: string): void {
    // A user-resolved address closes the initial-seed window, so the parent
    // feeding the picked street back into [initialAddress] can't re-run the seed
    // effect and auto-tick "keine Strassennummer" for a numberless suggestion.
    this.addressSeeded = true;
    this.lastLocation = location;
    // A fresh resolution overwrites the fields with the geocoder's values; the
    // user can still edit them afterwards.
    this.zipcodeControl.setValue(location.zipcode ?? '', { emitEvent: false });
    this.cityControl.setValue(location.city ?? '', { emitEvent: false });
    this.resolvedCity = location.city ?? '';
    this.resolvedStreet = (location.street ?? '').trim();
    this.resolvedBox = (displayText ?? location.street ?? '').trim();
    const number = (location.streetNumber ?? '').trim();
    // A pick that carries a number overrides a stale "keine Strassennummer"
    // (left from a stored address) — otherwise the tick would strip the
    // number the user just typed.
    if (number && this.noNumberControl.value) {
      this.noNumberControl.setValue(false, { emitEvent: false });
      this.numberControl.enable({ emitEvent: false });
    }
    if (!this.noNumberControl.value) {
      this.numberControl.setValue(number, { emitEvent: false });
    }
    this.refreshStreetDisplay();
    this.addressControl.updateValueAndValidity({ emitEvent: false });
    if (this.addressControl.hasError('missingNumber')) {
      this.addressControl.markAsTouched();
    }
    this.emitMerged();
  }

  /** The box and the echo show the street with its number, or without it
   * while "keine Strassennummer" is ticked — the user just said the number
   * doesn't belong, so it must not be stored as part of `street` either. The
   * box is updated silently and re-marked as resolved, so the edit-detection
   * above doesn't read it as a new user edit. */
  private refreshStreetDisplay(): void {
    const number = (this.lastLocation.streetNumber ?? '').trim();
    let street = this.resolvedStreet;
    let box = this.resolvedBox;
    if (this.noNumberControl.value && number) {
      const escaped = number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const trailing = new RegExp(`\\s+${escaped}(?=,|$)`, 'i');
      street = street.replace(trailing, '');
      box = box.replace(trailing, '');
    }
    this.streetControl.setValue(street, { emitEvent: false });
    this.addressControl.setValue(box, { emitEvent: false });
    this.lastResolvedQuery = box;
  }

  /** Emit the resolved street (as echoed under "Strasse & Nr.") as `street`,
   * with the separate number alongside. */
  private emitMerged(): void {
    const street = (this.streetControl.value ?? '').trim();
    const noNum = !!this.noNumberControl.value;
    const number = noNum ? '' : (this.numberControl.value ?? '').trim();
    // PLZ/Ort mirror the resolved location; the rest of it — coords, canton,
    // Gemeinde — rides along via the spread.
    const zip = (this.zipcodeControl.value ?? '').trim();
    const ort = (this.cityControl.value ?? '').trim();
    this.locationResolved.emit({
      ...this.lastLocation,
      zipcode: zip || this.lastLocation.zipcode,
      city: ort || this.lastLocation.city,
      street: street || undefined,
      streetNumber: number || undefined,
    });
    this.emitComplete();
  }

  /**
   * Reveal the red state on every field this component owns. Called by parents
   * from `revealInvalidFields` when a save is attempted with the address
   * incomplete — the fields are otherwise untouched, so nothing would go red.
   */
  markInvalidFieldsTouched(): void {
    this.addressControl.markAsTouched();
    this.numberControl.markAsTouched();
  }

  /** The red line below the field row — the only feedback for an unresolved
   * address, shown once the box is touched (a blur, or a no-results lookup). */
  showUnresolved(): boolean {
    return (
      this.addressControl.hasError('unresolved') && this.addressControl.touched
    );
  }

  /** No suggestion (or no geocode) for what was typed: go red immediately
   * instead of waiting for the blur that would normally mark the box touched. */
  private flagNoResults(): void {
    this.noResults = true;
    this.addressControl.markAsTouched();
  }

  hasTypedAddress(): boolean {
    return (
      typeof this.addressControl.value === 'string' &&
      !!this.addressControl.value.trim()
    );
  }

  private isResolved(): boolean {
    return !!this.lastLocation.cantonShort || this.seededFromStore;
  }

  private emitComplete(): void {
    const noNum = !!this.noNumberControl.value;
    const hasNumber =
      !!(this.numberControl.value ?? '').trim() &&
      !this.numberControl.hasError('pattern');
    const numberOk = !this.requireNumber() || noNum || hasNumber;
    this.addressComplete.emit(numberOk && this.isResolved());
  }
}
